import React, { useMemo } from 'react';
import {
  Box,
  capitalizeFirstLetter,
  EnrollmentStatus,
  FlexListHeader,
  Inscription,
  Status,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { ListItemBase } from '@/common/types';

import { PolicyStatus } from '../../../EnrollmentDetails/queries/useGetPolicyStatuses.query';

export const DateFromTo: React.FC<{ date?: string; isDateTo?: boolean; 'data-testid'?: string }> = ({
  date,
  isDateTo,
  'data-testid': dataTestId,
}) => {
  const isForever = useMemo(() => {
    if (isDateTo) {
      return dayjs(date).isAfter(dayjs(`01/01/${dayjs().year() + 100}`));
    }

    return false;
  }, [isDateTo, date]);

  return (
    <Inscription size="small" lineHeight="18px" style={{ fontWeight: 500 }} data-testid={dataTestId}>
      {isForever ? 'Forever' : dayjs(date).format(DEFAULT_DATE_FORMAT)}
    </Inscription>
  );
};

export const checkIsInRange = (validFrom: string, validTo: string | null) => {
  const today = dayjs().startOf('day');
  const fromDate = dayjs(validFrom).startOf('day');

  if (validTo) {
    const toDate = dayjs(validTo).startOf('day');
    return (
      (today.isAfter(fromDate) || today.isSame(fromDate)) && (today.isBefore(toDate) || today.isSame(toDate))
    );
  }

  return today.isAfter(fromDate) || today.isSame(fromDate);
};

export const getBorderColor = (el: PolicyStatus) => {
  if (el.status === EnrollmentStatus.Active && checkIsInRange(el.validFrom, el.validTo)) {
    return 'successBorder';
  } else if (el.status === EnrollmentStatus.Canceled && checkIsInRange(el.validFrom, el.validTo)) {
    return 'dangerBorder';
  } else if (el.status === EnrollmentStatus.Expired && checkIsInRange(el.validFrom, el.validTo)) {
    return 'dangerBorder';
  } else if (el.status === EnrollmentStatus.Future && checkIsInRange(el.validFrom, el.validTo)) {
    return 'futureContainer';
  }
  return 'border2';
};

const FOREVER_LABEL = 'Forever';
const ERROR_STATUS = 'error';

const Wrapper = styled(Box)`
  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacings.spacing4};
  }
`;

const EmploymentStatuses: React.FC<{
  statuses: PolicyStatus[];
}> = ({ statuses }) => {
  const headers: ListItemBase[] = useMemo(() => {
    const currentHeaders = [
      {
        key: 'status',
        title: 'Status',
      },
      {
        key: 'period',
        title: 'Period',
        flex: 1.5,
      },
    ];
    return currentHeaders;
  }, []);

  return (
    <Box
      data-testid="enrollment-statuses-wrapper"
      background="module"
      pad="xs"
      round="container2Round"
      // margin={{ vertical: 'xs' }}
    >
      <FlexListHeader items={headers} thinMode />
      {statuses.map((item, index) => {
        const isCurrent = checkIsInRange(item.validFrom, item.validTo);
        return (
          <Wrapper
            key={item.id}
            height="36px"
            background="canvas"
            round="dropdownItemRound"
            margin={{ top: index === 0 ? 'spacing4' : '0px' }}
            pad={{ vertical: 'spacing8', horizontal: 'spacing24' }}
            direction="row"
            align="center"
            border={{ size: '2px', color: getBorderColor(item) }}
            data-testid={`enrollment-status-row-${index}`}
          >
            <Box direction="row" align="center" width="40%">
              {isCurrent && (
                <Box align="center" justify="center" margin={{ right: 'spacing4' }}>
                  <Status
                    circleOnly
                    status={item?.status === EnrollmentStatus.Canceled ? ERROR_STATUS : item?.status}
                    testId="status-indicator"
                  />
                </Box>
              )}
              <Inscription
                size="small"
                lineHeight="18px"
                style={{ fontWeight: isCurrent ? 700 : 500 }}
                data-testid={`enrollment-status-row-${index}_status`}
              >
                {item?.status && capitalizeFirstLetter(item?.status)}
              </Inscription>
              {isCurrent && (
                <Box
                  height="18px"
                  background="border1"
                  pad={{ horizontal: 'xxs' }}
                  margin={{ left: 'xs' }}
                  round="button2Round"
                  data-testid={`enrollment-status-row-${index}_is-current`}
                >
                  <Inscription size="10px" weight={500} lineHeight="18px">
                    Current
                  </Inscription>
                </Box>
              )}
            </Box>
            <Box
              height="24px"
              direction="row"
              align="center"
              data-testid={`enrollment-status-row-${index}_period`}
            >
              <DateFromTo date={item.validFrom} data-testid={`enrollment-status-row-${index}_period-start`} />
              &nbsp;-&nbsp;
              {item.validTo ? (
                <DateFromTo
                  date={item.validTo}
                  isDateTo
                  data-testid={`enrollment-status-row-${index}_period-end`}
                />
              ) : (
                <Inscription size="small" lineHeight="18px" style={{ fontWeight: 500 }}>
                  {FOREVER_LABEL}
                </Inscription>
              )}
            </Box>
          </Wrapper>
        );
      })}
    </Box>
  );
};

export default EmploymentStatuses;
